import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setExpanded } from '../redux/slices/musicPlayerSlice';
import { setActiveSearch, setTrackQuery, setMixQuery } from '../redux/slices/djpalSlice';
import { setActiveTab } from '../redux/slices/appSlice';
import { searchTrack, searchMixes } from '../redux/thunk/djpalThunk';
import LikeButton from './LikeButton';
import AddToPlaylistModal from './AddToPlaylistModal';
import { isNative } from '../utils/webOnly';
import { getApplePageUrl } from '../utils/applePageUrlCache';
import { getNativePlaybackEngine } from '../playback/NativePlaybackEngine';
import { upscaleArtwork } from '../utils/cover';
import './NowPlayingPage.scss';

// Library root selector — all button proxies are scoped below it so
// desktop DOM stays identical.
const LIB_ROOT = '.react-jinke-music-player-main';
const clickLibButton = (selector) => {
    const el = document.querySelector(selector);
    if (el) el.click();
};

// upscaleArtwork moved to utils/cover.js — it had drifted into three copies
// with three different regexes.

const NowPlayingPage = () => {
    const dispatch = useDispatch();
    const expanded = useSelector((s) => s.musicPlayer.expanded);
    const currentTrack = useSelector((s) => s.musicPlayer.currentTrack);
    const isPlaying = useSelector((s) => s.musicPlayer.isPlaying);
    const autoRadio = useSelector((s) => s.musicPlayer.autoRadio);
    const audioList = useSelector((s) => s.musicPlayer.audioList);

    const [progress, setProgress] = useState({ t: 0, d: 0 });
    const [menuOpen, setMenuOpen] = useState(false);
    const [showPlaylistModal, setShowPlaylistModal] = useState(false);
    const menuRef = useRef(null);
    const progressHitRef = useRef(null);
    // While the user is dragging the progress bar, render the fill from
    // the drag position instead of the polled audio.currentTime so the
    // bar moves with the finger even before the audio element catches
    // up. `null` means "not dragging — use polled progress".
    const [seekT, setSeekT] = useState(null);
    // A just-committed seek awaiting engine catch-up. On native the seek
    // round-trips through the plugin and the muted mirror element keeps
    // reporting the OLD position for a few polls — without this hold the
    // bar visibly snaps back before landing where the user dropped it.
    const pendingSeekRef = useRef(null); // { t, ts } | null

    // Mini-player tap → expand. Delegates via document so the handler
    // survives jinke re-rendering the panel. Ignores taps on the
    // library's own buttons.
    useEffect(() => {
        const onDocClick = (e) => {
            if (expanded) return;
            const panel = e.target.closest(`${LIB_ROOT} .music-player-panel`);
            if (!panel) return;
            if (e.target.closest('button, svg, .play-btn, .prev-audio, .next-audio, .loop-btn, .reload-btn, .audio-lists-btn, .hide-panel, .play-mode-title, .rc-slider, input, a, .track-like-btn')) {
                return;
            }
            dispatch(setExpanded(true));
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, [expanded, dispatch]);

    // The plugin's clock (native only; 0 on web/Electron). Held in a ref so
    // the polling loop below sees fresh values without being torn down and
    // rebuilt four times a second.
    const nativePosition = useSelector((s) => s.musicPlayer.position);
    const nativeDuration = useSelector((s) => s.musicPlayer.duration);
    const nativeClockRef = useRef({ position: 0, duration: 0 });
    nativeClockRef.current = { position: nativePosition, duration: nativeDuration };

    // Paint the CURRENT position on the very first frame the page is shown.
    //
    // `progress` is component state that survives between visits, and the poll
    // below only refreshes it from an effect — which runs AFTER paint. So
    // reopening the page showed the value from the last time you looked at it
    // and then visibly corrected. useLayoutEffect runs before the browser
    // paints, so there is no stale frame to see.
    useLayoutEffect(() => {
        if (!expanded) return;
        const nat = isNative ? nativeClockRef.current : null;
        const audio = window.musicPlayerInstance;
        const t = (nat && Number.isFinite(nat.position) ? nat.position : audio?.currentTime) || 0;
        const natDur = nat && Number.isFinite(nat.duration) && nat.duration > 0 ? nat.duration : null;
        const d = natDur || (audio && Number.isFinite(audio.duration) ? audio.duration : 0);
        setProgress({ t, d });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded]);

    // Poll audio progress while expanded.
    useEffect(() => {
        if (!expanded) return undefined;
        let stopped = false;
        const tick = () => {
            if (stopped) return;
            const audio = window.musicPlayerInstance;
            if (audio) {
                // NATIVE: the plugin's clock, not the element's. jinke's
                // <audio> plays nothing here — its currentTime only moves when
                // MusicPlayer writes the plugin's position onto it, and only
                // once they drift more than 1.5s. Polling it made the bar open
                // at a stale value and then visibly step to catch up. Redux
                // carries the plugin's own position, updated at 4Hz.
                const nat = isNative ? nativeClockRef.current : null;
                let t = (nat && Number.isFinite(nat.position) ? nat.position : audio.currentTime) || 0;
                const pend = pendingSeekRef.current;
                if (pend) {
                    // Release the hold once the engine reports (near) the
                    // committed position, or after a timeout so a failed
                    // seek can't freeze the bar.
                    if (Math.abs(t - pend.t) < 1.5 || Date.now() - pend.ts > 3000) pendingSeekRef.current = null;
                    else t = pend.t;
                }
                const natDur = nat && Number.isFinite(nat.duration) && nat.duration > 0 ? nat.duration : null;
                setProgress({
                    t,
                    d: natDur || (Number.isFinite(audio.duration) ? audio.duration : 0),
                });
            }
            setTimeout(tick, 500);
        };
        tick();
        return () => { stopped = true; };
    }, [expanded]);

    // Tap/drag-to-seek on the progress bar. Pointer events handle mouse
    // and touch in one path; setPointerCapture lets the drag continue
    // even if the finger leaves the bar's vertical hit area.
    //
    // Duration fallback: on native, jinke's <audio> is muted and may not
    // load metadata, so audio.duration can be NaN. The native plugin's
    // duration is mirrored into Redux via `playerState`, so we use that
    // as a fallback. Without this, scrubbing on native silently no-ops
    // because seekFromClientX returns null on NaN.
    const seekFromClientX = useCallback((clientX) => {
        const audio = window.musicPlayerInstance;
        const audioDur = audio && Number.isFinite(audio.duration) ? audio.duration : 0;
        const reduxDur = window.__reduxStore?.getState?.()?.musicPlayer?.duration || 0;
        const dur = audioDur > 0 ? audioDur : reduxDur;
        if (dur <= 0) return null;
        const track = progressHitRef.current?.querySelector('.now-playing-progress-track');
        const rect = (track || progressHitRef.current)?.getBoundingClientRect();
        if (!rect || rect.width <= 0) return null;
        const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        return ratio * dur;
    }, []);

    const handleProgressPointerDown = useCallback((e) => {
        e.preventDefault();
        const t = seekFromClientX(e.clientX);
        if (t == null) return;
        setSeekT(t);
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    }, [seekFromClientX]);

    const handleProgressPointerMove = useCallback((e) => {
        // Only react while a drag is in progress — buttons === 0 means
        // a hover-move on desktop, which we ignore.
        if (seekT == null) return;
        const t = seekFromClientX(e.clientX);
        if (t != null) setSeekT(t);
    }, [seekT, seekFromClientX]);

    const commitSeek = useCallback((e) => {
        if (seekT == null) return;
        if (isNative) {
            // Seek the PLUGIN only. Writing jinke's element as well pushed it
            // to the same offset, and dragging near the end therefore ended
            // that element — jinke reacted by restarting, so a scrub to the
            // end replayed the track instead of finishing it. The element is
            // silent here and its clock is not what anything reads.
            try { getNativePlaybackEngine().seek(seekT); } catch {}
        } else {
            const audio = window.musicPlayerInstance;
            if (audio) {
                try { audio.currentTime = seekT; } catch {}
            }
        }
        // Show the committed position immediately and hold it against stale
        // polls until the engine catches up (see pendingSeekRef).
        pendingSeekRef.current = { t: seekT, ts: Date.now() };
        setProgress((p) => ({ ...p, t: seekT }));
        setSeekT(null);
        if (e?.currentTarget?.releasePointerCapture) {
            try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
        }
    }, [seekT]);

    // Close the ⋯ menu on outside click / Escape.
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onDown = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('touchstart', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('touchstart', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [menuOpen]);

    const close = () => dispatch(setExpanded(false));

    const togglePlay = () => clickLibButton(`${LIB_ROOT} .play-btn`);
    const next = () => clickLibButton(`${LIB_ROOT} .next-audio`);
    const prev = () => clickLibButton(`${LIB_ROOT} .prev-audio`);
    const toggleInfinity = () => clickLibButton(`${LIB_ROOT} .reload-btn`);
    const openQueue = () => clickLibButton(`${LIB_ROOT} .audio-lists-btn`);

    // The audioList entry carries the extra metadata we need to decide
    // whether the current source is a mix (mixId, mixTitle, link).
    // currentTrack itself only has the minimal jinke shape.
    const audioEntry = currentTrack?.musicSrc
        ? audioList.find((t) => t.musicSrc === currentTrack.musicSrc)
        : null;
    const isMix = !!audioEntry?.isMix;

    // ── Hero artwork: show something instantly, sharpen when it arrives ──
    //
    // The hero renders the UPSCALED url (600x600 on Apple), which is a
    // different resource from the ~100px thumbnail every feed row and the
    // mini-player already fetched. Pointing <img> straight at it meant the
    // slot was empty from the moment the page opened until that cold request
    // landed — the "artwork lags when I open a track I'm already playing".
    //
    // So: render the thumbnail, which is already in cache and paints on the
    // first frame, and swap to the hero only once it has fully decoded. The
    // swap is same-image-sharper, so there is nothing to flash.
    //
    // Declared above the `!expanded` early return — these are hooks.
    const rawCover = audioEntry?.artwork
        || audioEntry?.artworkUrl
        || audioEntry?.cover
        || currentTrack?.cover
        || '';
    const heroCover = useMemo(() => upscaleArtwork(rawCover), [rawCover]);
    const [readyCover, setReadyCover] = useState(rawCover);
    useEffect(() => {
        setReadyCover(rawCover);
        if (!heroCover || heroCover === rawCover) return undefined;
        let cancelled = false;
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => { if (!cancelled) setReadyCover(heroCover); };
        // On error keep the thumbnail — a soft cover beats an empty square.
        img.src = heroCover;
        return () => { cancelled = true; };
    }, [rawCover, heroCover]);

    // Sub-tab + query BEFORE setActiveTab('djpal') so the nav-history marker
    // pushed on the tab switch is typed and the search result REPLACES it —
    // one Back returns to the originating page, not Mixpedia home.
    const openInMixpedia = () => {
        if (!currentTrack?.name) return;
        if (isMix) {
            const rawTitle = audioEntry?.mixTitle || currentTrack.name;
            const dj = currentTrack.singer || '';
            const displayQuery = dj ? `${dj} - ${rawTitle}` : rawTitle;
            // Id-first, no text-search fallback — mirrors the desktop player
            // bar (MusicPlayer.handleSearchInMixpedia): mixId is the real
            // catalog id, originalUrl is the exact-key fallback for
            // non-catalog mixes.
            const filters = audioEntry?.mixId ? { public_id: audioEntry.mixId }
                : audioEntry?.originalUrl ? { link: audioEntry.originalUrl }
                : null;
            if (!filters) return;
            dispatch(setActiveSearch('mix'));
            dispatch(setMixQuery(displayQuery));
            dispatch(setActiveTab('djpal'));
            dispatch(searchMixes({ filters }));
        } else {
            const query = currentTrack.singer
                ? `${currentTrack.singer} - ${currentTrack.name}`
                : currentTrack.name;
            dispatch(setActiveSearch('track'));
            dispatch(setTrackQuery(query));
            dispatch(setActiveTab('djpal'));
            dispatch(searchTrack({ trackName: query, trackId: currentTrack.trackId || null, variant: 'A', artworkUrl: currentTrack.cover || null }));
        }
        setMenuOpen(false);
        close();
    };

    const openAddToPlaylist = () => {
        setMenuOpen(false);
        setShowPlaylistModal(true);
    };

    // "Listen on Apple Music" — points the user to the licensed source for the
    // full track (we only play Apple's preview). Reinforces the discovery →
    // store model behind the 30s previews (App Review 5.2.3).
    const openInAppleMusic = useCallback(() => {
        // currentTrack.musicSrc is often EMPTY on native, so the musicSrc match
        // fails — but the playing track is the entry in audioList (reset to a
        // single entry on sample play). Read pageUrl from there. Fall back to
        // an Apple Music search when a track has no stored deep link.
        const playing = (currentTrack?.musicSrc && audioList.find((t) => t.musicSrc === currentTrack.musicSrc))
            || (audioList.length === 1 ? audioList[0] : null);
        const direct = playing?.pageUrl
            || getApplePageUrl(currentTrack?.musicSrc)
            || getApplePageUrl(playing?.musicSrc);
        if (direct) {
            try { window.electron.openUrl(direct); } catch (_) {}
            return;
        }
        const t = currentTrack?.name || playing?.name;
        const a = currentTrack?.singer || playing?.singer || '';
        if (!t) return;
        const term = encodeURIComponent(`${a} ${t}`.trim());
        try { window.electron.openUrl(`https://music.apple.com/us/search?term=${term}`); } catch (_) {}
    }, [currentTrack, audioList]);

    // Build the shape LikeButton / AddToPlaylistModal expect from the
    // jinke audioInfo we stashed in Redux. singer → artist, name → title.
    const trackForModal = useMemo(() => (currentTrack
        ? { id: currentTrack.id || null, artist: currentTrack.singer || '', title: currentTrack.name || '' }
        : null
    ), [currentTrack]);

    if (!expanded) return null;

    // While dragging, render the fill from the in-flight seek position
    // so the bar tracks the finger immediately. Polling re-takes over
    // once the drag ends.
    const displayT = seekT != null ? seekT : progress.t;
    const pct = progress.d > 0 ? Math.min(100, (displayT / progress.d) * 100) : 0;
    const fmt = (s) => {
        if (!Number.isFinite(s) || s < 0) return '0:00';
        const m = Math.floor(s / 60);
        const r = Math.floor(s % 60);
        return `${m}:${String(r).padStart(2, '0')}`;
    };

    // Whichever of {thumbnail, hero} is actually loaded — see the effect above.
    const cover = readyCover;
    const title = currentTrack?.name || 'Nothing playing';
    const artist = currentTrack?.singer || '';

    return (
        <div className="now-playing-page">
            <div className="now-playing-header">
                <button className="now-playing-icon-btn" onClick={close} aria-label="Minimize player">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>
                <span className="now-playing-header-label">Now Playing</span>
                <div className="now-playing-menu-wrap" ref={menuRef}>
                    <button className="now-playing-icon-btn" onClick={() => setMenuOpen((v) => !v)} aria-label="More">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="5" cy="12" r="2" />
                            <circle cx="12" cy="12" r="2" />
                            <circle cx="19" cy="12" r="2" />
                        </svg>
                    </button>
                    {menuOpen && (
                        <div className="now-playing-menu">
                            <button className="now-playing-menu-item" onClick={openInMixpedia}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="7" />
                                    <line x1="21" y1="21" x2="16.5" y2="16.5" />
                                </svg>
                                Open in Mixpedia
                            </button>
                            {!isMix && trackForModal?.artist && (
                                <button className="now-playing-menu-item" onClick={openAddToPlaylist}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    Add to playlist
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="now-playing-artwork" onClick={openInMixpedia}>
                {cover
                    ? <img src={cover} alt={title} />
                    : <div className="now-playing-artwork-placeholder">&#9835;</div>
                }
            </div>

            <div className="now-playing-meta">
                <div className="now-playing-meta-text" onClick={openInMixpedia}>
                    <div className="now-playing-title" title={title}>{title}</div>
                    {artist && <div className="now-playing-artist" title={artist}>{artist}</div>}
                </div>
                {trackForModal && trackForModal.artist && (
                    <div className="now-playing-meta-like">
                        <LikeButton track={trackForModal} />
                    </div>
                )}
            </div>

            <div className="now-playing-progress">
                <div
                    ref={progressHitRef}
                    className={`now-playing-progress-hit${seekT != null ? ' seeking' : ''}`}
                    // role="slider" (with data-swipe-ignore as belt-and-braces)
                    // matches the iOS swipe-nav IGNORE_SEL in App.js — without
                    // it a horizontal scrub also commits a back/forward swipe.
                    role="slider"
                    aria-label="Seek"
                    aria-valuemin={0}
                    aria-valuemax={Math.round(progress.d) || 0}
                    aria-valuenow={Math.round(displayT) || 0}
                    data-swipe-ignore="true"
                    onPointerDown={handleProgressPointerDown}
                    onPointerMove={handleProgressPointerMove}
                    onPointerUp={commitSeek}
                    onPointerCancel={commitSeek}
                >
                    <div className="now-playing-progress-track">
                        <div className="now-playing-progress-fill" style={{ width: `${pct}%` }} />
                    </div>
                </div>
                <div className="now-playing-progress-times">
                    <span>{fmt(displayT)}</span>
                    <span>{progress.d > 0 ? `-${fmt(Math.max(0, progress.d - displayT))}` : '--:--'}</span>
                </div>
            </div>

            <div className="now-playing-controls">
                <button
                    className={`now-playing-ctrl${autoRadio ? ' on' : ''}`}
                    onClick={toggleInfinity}
                    aria-label="Infinity"
                >
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.585 0-4.585 8 0 8 5.606 0 7.644-8 12.74-8z" />
                    </svg>
                </button>
                <button className="now-playing-ctrl" onClick={prev} aria-label="Previous">
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor">
                        <polygon points="19 20 9 12 19 4 19 20" />
                        <rect x="5" y="4" width="2" height="16" />
                    </svg>
                </button>
                <button className="now-playing-play" onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
                    {isPlaying ? (
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                            <rect x="6" y="4" width="4" height="16" rx="1" />
                            <rect x="14" y="4" width="4" height="16" rx="1" />
                        </svg>
                    ) : (
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="6 3 20 12 6 21 6 3" />
                        </svg>
                    )}
                </button>
                <button className="now-playing-ctrl" onClick={next} aria-label="Next">
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor">
                        <polygon points="5 4 15 12 5 20 5 4" />
                        <rect x="17" y="4" width="2" height="16" />
                    </svg>
                </button>
                <button
                    className="now-playing-ctrl"
                    onClick={openInMixpedia}
                    aria-label="Open in Mixpedia"
                >
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="7" />
                        <line x1="21" y1="21" x2="16.5" y2="16.5" />
                    </svg>
                </button>
            </div>

            <div className="now-playing-utility">
                <button className="now-playing-util-btn" onClick={openQueue} aria-label="Queue">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="8" y1="6" x2="21" y2="6" />
                        <line x1="8" y1="12" x2="21" y2="12" />
                        <line x1="8" y1="18" x2="21" y2="18" />
                        <circle cx="3.5" cy="6" r="0.8" fill="currentColor" />
                        <circle cx="3.5" cy="12" r="0.8" fill="currentColor" />
                        <circle cx="3.5" cy="18" r="0.8" fill="currentColor" />
                    </svg>
                </button>
            </div>

            {isNative && currentTrack?.name && (
                <button
                    type="button"
                    onClick={openInAppleMusic}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8, alignSelf: 'center',
                        margin: '6px auto 0', padding: '9px 18px', borderRadius: 22,
                        background: 'transparent', border: '1px solid var(--color-border)',
                        color: 'var(--color-text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 17V4l10-2v13" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/><circle cx="6" cy="17" r="3"/><circle cx="16" cy="15" r="3"/></svg>
                    Listen on Apple Music
                </button>
            )}

            {showPlaylistModal && trackForModal && ReactDOM.createPortal(
                <AddToPlaylistModal
                    show={showPlaylistModal}
                    onHide={() => setShowPlaylistModal(false)}
                    track={trackForModal}
                />,
                document.body
            )}
        </div>
    );
};

export default NowPlayingPage;
